// @flow
import { API_CATEGORIES, MMB_KEYS, type MMBKeys } from "./booking"

export const ADDITIONAL_SERVICES_ALLOWED_TO_CHANGE_CONFIG = 48

export type ViaSupportCategory =
  | "allocated_seating"
  | "sports_equipment"
  | "travelling_with_pets"
  | "musical_equipment"

export type Category =
  | ViaSupportCategory
  | "baggage"
  | "insurance"
  | "change_flights"
  | "special_assistance"
  | "accommodation"
  | "car_rental"
  | "uber"
  | "transfer"
  | "airport_parking"
  | "price_change"
  | "scheduleChange"
  | "extra"
  | "passenger_edit"
  | "assisted_refund"
  | "meals_on_board"
  | "offered_meals"
  | "priority_boarding"
  | "infant"

// Order matters
export const SERVICES: { [MMBKeys]: Category } = Object.freeze({
  [MMB_KEYS.BAGGAGE]: "baggage",
  [MMB_KEYS.SEATING]: "allocated_seating",
  [MMB_KEYS.INSURANCE]: "insurance",
  [MMB_KEYS.ACCOMMODATION]: "accommodation",
  [MMB_KEYS.AIRPORT_PARKING]: "airport_parking",
  [MMB_KEYS.MEALS]: "meals_on_board",
  [MMB_KEYS.OFFERED_MEALS]: "offered_meals",
  [MMB_KEYS.TRANSFER]: "transfer",
  [MMB_KEYS.SPECIAL_ASSISTANCE]: "special_assistance",
  [MMB_KEYS.SPORTS_EQUIPMENT]: "sports_equipment",
  [MMB_KEYS.UBER]: "uber",
  [MMB_KEYS.PETS]: "travelling_with_pets",
  [MMB_KEYS.MUSICAL_EQUIPMENT]: "musical_equipment",
  [MMB_KEYS.CAR_RENTAL]: "car_rental",
  // statuses - not displayed
  [MMB_KEYS.EXTRA]: "extra",
  [MMB_KEYS.PRICE_CHANGE]: "price_change",
  [MMB_KEYS.SCHEDULE_CHANGE]: "scheduleChange",
  [MMB_KEYS.CHANGE_FLIGHTS]: "change_flights",
  [MMB_KEYS.PASSENGER_EDIT]: "passenger_edit",
  [MMB_KEYS.ASSISTED_REFUND]: "assisted_refund",
  [MMB_KEYS.PRIORITY_BOARDING]: "priority_boarding",
  [MMB_KEYS.INFANT]: "infant",
})

export type Service = $Values<typeof SERVICES>

export const API_CATEGORIES_TO_SERVICES = Object.freeze({
  [API_CATEGORIES.ALLOCATED_SEATING]: SERVICES.SEATING,
  [API_CATEGORIES.ASSISTED_REFUND]: SERVICES.EXTRA,
  [API_CATEGORIES.BAGGAGE]: SERVICES.BAGGAGE,
  [API_CATEGORIES.BAGS]: SERVICES.BAGGAGE,
  [API_CATEGORIES.BOOKING_RESTORE]: SERVICES.EXTRA,
  [API_CATEGORIES.EXTRA_BAGS]: SERVICES.BAGGAGE,
  [API_CATEGORIES.EXTRAS_CHANGE_FLIGHT]: SERVICES.CHANGE_FLIGHTS,
  [API_CATEGORIES.EXTRAS_OTHER]: SERVICES.EXTRA,
  [API_CATEGORIES.EXTRAS_PASSENGER_EDIT]: SERVICES.PASSENGER_EDIT,
  [API_CATEGORIES.FLIGHTS]: SERVICES.CHANGE_FLIGHTS,
  [API_CATEGORIES.INFANT]: SERVICES.INFANT,
  [API_CATEGORIES.MEALS]: SERVICES.MEALS,
  [API_CATEGORIES.MEALS_ON_BOARD]: SERVICES.OFFERED_MEALS,
  [API_CATEGORIES.MUSICAL_EQUIPMENT]: SERVICES.MUSICAL_EQUIPMENT,
  [API_CATEGORIES.OTHER]: SERVICES.EXTRA,
  [API_CATEGORIES.PASSENGER_EDIT]: SERVICES.PASSENGER_EDIT,
  [API_CATEGORIES.PETS]: SERVICES.PETS,
  [API_CATEGORIES.PRICE_CHANGE]: SERVICES.PRICE_CHANGE,
  [API_CATEGORIES.PRIORITY_BOARDING]: SERVICES.PRIORITY_BOARDING,
  [API_CATEGORIES.SEATING]: SERVICES.SEATING,
  [API_CATEGORIES.SERVICE_BAGGAGE]: SERVICES.BAGGAGE,
  [API_CATEGORIES.SERVICE_INFANT]: SERVICES.INFANT,
  [API_CATEGORIES.SERVICE_MEALS_ON_BOARD]: SERVICES.MEALS,
  [API_CATEGORIES.SERVICE_MUSICAL_EQUIPMENT]: SERVICES.MUSICAL_EQUIPMENT,
  [API_CATEGORIES.SERVICE_OTHER]: SERVICES.EXTRA,
  [API_CATEGORIES.SERVICE_PETS]: SERVICES.PETS,
  [API_CATEGORIES.SERVICE_PRIORITY_BOARDING]: SERVICES.PRIORITY_BOARDING,
  [API_CATEGORIES.SERVICE_SEATING]: SERVICES.SEATING,
  [API_CATEGORIES.SERVICE_SPECIAL_ASSISTANCE]: SERVICES.SPECIAL_ASSISTANCE,
  [API_CATEGORIES.SERVICE_SPORTS_EQUIPMENT]: SERVICES.SPORTS_EQUIPMENT,
  [API_CATEGORIES.SPECIAL_ASSISTANCE]: SERVICES.SPECIAL_ASSISTANCE,
  [API_CATEGORIES.SPORTS_EQUIPMENT]: SERVICES.SPORTS_EQUIPMENT,
  [API_CATEGORIES.UPDATE_INSURANCES]: SERVICES.INSURANCE,
})

export type ApiCategoriesToServices = $Values<typeof API_CATEGORIES_TO_SERVICES>

export const SERVICE_AVAILABILITY = {
  FLIGHT_ONLY: "flight_only",
  ALL: "all",
}

export const SERVICES_STATUSES = Object.freeze({
  CANCELLED: "cancelled",
  CLOSED: "closed",
  CONFIRMED: "confirmed",
  EXPIRED: "expired",
  // INTEREST: "interest",
  OFFERED: "offered",
  OPEN: "open",
  PENDING: "pending",
  REFUNDED: "refunded",
  CREATED: "created", // custom status used only on frontend during payment
})

export const SERVICE_STATUS_TITLE_KEYS = {
  [SERVICES_STATUSES.CANCELLED]: __("account.change_status_cancelled"),
  [SERVICES_STATUSES.CLOSED]: __("account.change_status_processing"),
  [SERVICES_STATUSES.CONFIRMED]: __("account.change_status_confirmed"),
  [SERVICES_STATUSES.EXPIRED]: __("account.change_status_expired"),
  // [SERVICES_STATUSES.INTEREST]: __("booking.additional_services.statuses.interest.title"), // TODO: translate once backend supports requested Meals, Equipment etc.
  // translation: Service Requested
  [SERVICES_STATUSES.OFFERED]: __("booking.additional_services.statuses.offer.title"),
  [SERVICES_STATUSES.OPEN]: __("account.change_status_open"),
  [SERVICES_STATUSES.PENDING]: __("account.change_status_processing"),
  [SERVICES_STATUSES.REFUNDED]: __("booking.additional_services.statuses.refunded.title"),
  [SERVICES_STATUSES.CREATED]: "Created", // placeholder for flow, not used
}

export const SERVICE_STATUS_DESCRIPTION_KEYS = {
  [SERVICES_STATUSES.CANCELLED]: __("booking.additional_services.statuses.cancelled.description"),
  [SERVICES_STATUSES.CLOSED]: __("booking.additional_services.statuses.pending.description"), // same as pending for now
  [SERVICES_STATUSES.CONFIRMED]: __("booking.additional_services.statuses.confirmed.description"),
  [SERVICES_STATUSES.EXPIRED]: __("booking.additional_services.statuses.expired.description"),
  // [SERVICES_STATUSES.INTEREST]: __("booking.additional_services.statuses.interest.description"), // TODO: translate once backend supports requested Meals, Equipment etc.
  // translation: Additional service was requested. We’ll let you know within 72 hours.
  [SERVICES_STATUSES.OFFERED]: __("booking.additional_services.statuses.offer.description"),
  [SERVICES_STATUSES.OPEN]: __("booking.additional_services.statuses.open.description"),
  [SERVICES_STATUSES.PENDING]: __("booking.additional_services.statuses.pending.description"),
  [SERVICES_STATUSES.REFUNDED]: __("booking.additional_services.statuses.refunded.description"),
  [SERVICES_STATUSES.CREATED]: "Created", // placeholder for flow, not used
}

// eslint-disable-next-line flowtype/no-weak-types
export const ADDITIONAL_SERVICES: { [Category]: Object } = {
  [SERVICES.CHANGE_FLIGHTS]: {
    name: __("change_flights.action"),
  },
  [SERVICES.PASSENGER_EDIT]: {
    name: __("booking.payment.summary.passenger_changes"),
  },
  [SERVICES.SEATING]: {
    name: __("booking.additional_services.service.allocated_seating.name"),
    cta: __("mmb.additional_services.cta_button.allocated_seating"),
    description: __("booking.additional_services.service.allocated_seating.description"),
    label: __("booking.additional_services.allocated_seating.label"),
    group_note: __("booking.additional_services.allocated_seating.group_note"),
    one_required: __("booking.additional_services.allocated_seating.one_required"),
    options: {
      window: __("booking.additional_services.allocated_seating.options.window"),
      aisle: __("booking.additional_services.allocated_seating.options.aisle"),
      emergency: __("booking.additional_services.allocated_seating.options.emergency"),
      group: __("booking.additional_services.allocated_seating.options.group"),
    },
    availability: SERVICE_AVAILABILITY.FLIGHT_ONLY,
    illustration: "Seating",
  },
  [SERVICES.INSURANCE]: {
    name: __("booking.additional_services.service.insurance.name"),
    cta: __("booking.additional_services.cta_button.insurance"),
    availability: SERVICE_AVAILABILITY.ALL,
    illustration: "Insurance",
  },
  [SERVICES.BAGGAGE]: {
    name: __("booking.additional_services.service.baggage.name"),
    cta: __("booking.additional_services.cta_button.baggage"),
    availability: SERVICE_AVAILABILITY.ALL,
    illustration: "CabinBaggage",
  },
  [SERVICES.MEALS]: {
    name: __("booking.additional_services.service.meals_on_board.name"),
    cta: __("booking.additional_services.cta_button.meals_on_board"),
    availability: SERVICE_AVAILABILITY.FLIGHT_ONLY,
    illustration: "Meal",
  },
  [SERVICES.OFFERED_MEALS]: {
    name: __("booking.additional_services.service.meals_on_board.name"),
    description: __("booking.additional_services.service.meals_on_board.description"),
  },
  [SERVICES.PETS]: {
    name: __("booking.additional_services.service.travelling_with_pets.name"),
    cta: __("mmb.additional_services.cta_pet"),
    description: __("booking.additional_services.service.travelling_with_pets.description"),
    label: __("booking.additional_services.travelling_with_pets.label"),
    placeholder: __("booking.additional_services.travelling_with_pets.placeholder"),
    dimensions: __("booking.additional_services.travelling_with_pets.dimensions"),
    availability: SERVICE_AVAILABILITY.FLIGHT_ONLY,
    illustration: "Pets",
  },
  [SERVICES.SPECIAL_ASSISTANCE]: {
    name: __("booking.additional_services.service.special_assistance.name"),
    cta: __("booking.additional_services.cta_button.special_assistance"),
    availability: SERVICE_AVAILABILITY.FLIGHT_ONLY,
    illustration: "SpecialAssistance",
  },
  [SERVICES.SPORTS_EQUIPMENT]: {
    name: __("booking.additional_services.service.sports_equipment.name"),
    cta: __("mmb.additional_services.cta_sports"),
    availability: SERVICE_AVAILABILITY.FLIGHT_ONLY,
    illustration: "SportsEquipment",
  },
  [SERVICES.MUSICAL_EQUIPMENT]: {
    name: __("booking.additional_services.service.musical_equipment.name"),
    cta: __("mmb.additional_services.cta_music"),
    description: __("booking.additional_services.service.musical_equipment.description"),
    label: __("booking.additional_services.musical_equipment.label"),
    placeholder: __("booking.additional_services.musical_equipment.placeholder"),
    dimensions: __("booking.additional_services.musical_equipment.dimensions"),
    protection: __("booking.additional_services.musical_equipment.protection"),
    availability: SERVICE_AVAILABILITY.FLIGHT_ONLY,
    illustration: "MusicalInstruments",
  },
  [SERVICES.ACCOMMODATION]: {
    name: __("booking.additional_services.service.accommodation.name"),
    description: __("booking.additional_services.service.accommodation.description"),
    availability: SERVICE_AVAILABILITY.ALL,
  },
  [SERVICES.CAR_RENTAL]: {
    name: __("booking.additional_services.service.car_rental.name"),
    cta: __("booking.additional_services.cta_button.car_rental"),
    description: __("booking.additional_services.service.car_rental.description"),
    availability: SERVICE_AVAILABILITY.ALL,
  },
  [SERVICES.EXTRA]: {
    name: __("booking.additional_services.service.extra.name"),
  },
  [SERVICES.PRICE_CHANGE]: {
    name: __("booking.process.price_change"),
  },
  [SERVICES.SCHEDULE_CHANGE]: {
    name: __("schedule_changes.changes.name"),
  },
  [SERVICES.AIRPORT_PARKING]: {
    name: __("booking.additional_services.service.airport_parking.name"),
    cta: __("booking.additional_services.cta_button.airport_parking"),
    availability: SERVICE_AVAILABILITY.ALL,
    illustration: "Parking",
  },
  [SERVICES.TRANSFER]: {
    name: __("booking.additional_services.service.transfer.name"),
    cta: __("booking.additional_services.cta_button.transfer"),
    availability: SERVICE_AVAILABILITY.ALL,
    illustration: "TransportBus",
  },
  [SERVICES.UBER]: {
    name: __("booking.additional_services.service.uber.name"),
    cta: __("booking.additional_services.cta_button.uber"),
    description: __("booking.additional_services.service.uber.description"),
    availability: SERVICE_AVAILABILITY.ALL,
  },
  [SERVICES.ASSISTED_REFUND]: {
    name: __("refund.calculator.selection.assisted_refund"),
  },
  [SERVICES.PRIORITY_BOARDING]: {
    name: __("booking.additional_services.service.priority_boarding"),
  },
  [SERVICES.INFANT]: {
    name: __("mmb.additional_services.service.infants"),
  },
}

export const getAdditionalServiceData = (service: Category) =>
  ADDITIONAL_SERVICES[service] || ADDITIONAL_SERVICES[SERVICES.EXTRA]

export const DEFAULT_VISIBLE_SERVICES_COUNT = 3

export const SEATING_OPTIONS = ["window", "aisle", "emergency", "group"]

export const SPORTS_EQUIPMENT_OPTIONS = [
  "golf_bags",
  "snowboards",
  "skis",
  "surfing",
  "bikes",
  "different",
]

export const PICKUP_DELAY = {
  BAGS: 60,
  NO_BAGS: 30,
}

export const AIRPORT_SECURITY_CHECK_DELAY = 180
